import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MERCHANT, TIER_PRICE } from '@neke/core';

/**
 * Юридические страницы: оферта, возврат, персональные данные, реквизиты.
 *
 * Без них не пройти модерацию ни в Kaspi Pay, ни у эквайринговых
 * агрегаторов — они открывают сайт и ищут, кто продавец, что именно
 * продаётся и как вернуть деньги.
 *
 * Тексты лежат в словарях и переключаются вместе со всем сайтом. Реквизиты
 * подставляются из @neke/core: на обоих языках они одинаковые.
 */

interface Section {
  h: string;
  p: string[];
}

const money = (value: number) => `${value.toLocaleString('ru-RU')} ₸`;

/**
 * Параметры для подстановки в юридические тексты.
 *
 * Экспортируется, потому что этим же набором пользуется тест: если добавить
 * переменную в словарь и забыть здесь, на живой странице останется «{{...}}».
 * Общий источник — единственный способ поймать такое до модерации.
 */
export const legalParams = {
  returnObjects: true,
  merchant: MERCHANT.name,
  bin: MERCHANT.bin,
  address: MERCHANT.address,
  email: MERCHANT.email,
  support: MERCHANT.support,
  phone: MERCHANT.phone,
  site: MERCHANT.site,
  price: money(TIER_PRICE.FULL),
} as const;

function LegalPage({ ns }: { ns: 'offer' | 'refund' | 'privacy' }) {
  const { t } = useTranslation();
  const sections = t(`${ns}.sections`, legalParams) as Section[];

  return (
    <div className="page">
      <h1>{t(`${ns}.title`)}</h1>
      <p className="fine">
        {t('legal.updated')} {MERCHANT.documentsUpdated}
      </p>

      {Array.isArray(sections) &&
        sections.map((section, i) => (
          <div className="stack legal-section" key={i}>
            {section.h && <h3>{section.h}</h3>}
            {section.p.map((text, j) => (
              <p className="fine" key={j}>
                {text}
              </p>
            ))}
          </div>
        ))}

      <div className="legal-links">
        <Link to="/offer">{t('legal.offer')}</Link>
        <Link to="/refund">{t('legal.refund')}</Link>
        <Link to="/privacy">{t('legal.privacy')}</Link>
        <Link to="/contacts">{t('legal.contacts')}</Link>
      </div>
    </div>
  );
}

export const Offer = () => <LegalPage ns="offer" />;
export const Refund = () => <LegalPage ns="refund" />;
export const Privacy = () => <LegalPage ns="privacy" />;

/** Реквизиты продавца отдельной страницей — модерация ищет именно её. */
export function Contacts() {
  const { t } = useTranslation();

  // Телефон и почту делаем ссылками: сайт открывают с телефона, и позвонить
  // или написать должно получаться в один тап, без выделения и копирования.
  const rows: Array<{ label: string; value: string; href?: string }> = [
    { label: t('contacts.name'), value: MERCHANT.name },
    { label: t('contacts.owner'), value: MERCHANT.owner },
    { label: t('contacts.bin'), value: MERCHANT.bin },
    { label: t('contacts.address'), value: MERCHANT.address },
    { label: t('contacts.phone'), value: MERCHANT.phone, href: `tel:${MERCHANT.phoneTel}` },
    { label: t('contacts.email'), value: MERCHANT.email, href: `mailto:${MERCHANT.email}` },
    {
      label: t('contacts.supportEmail'),
      value: MERCHANT.support,
      href: `mailto:${MERCHANT.support}`,
    },
    { label: t('contacts.helloEmail'), value: MERCHANT.hello, href: `mailto:${MERCHANT.hello}` },
  ];

  return (
    <div className="page">
      <h1>{t('contacts.title')}</h1>
      <p className="muted">{t('contacts.lead')}</p>

      <div className="card legal-req">
        {rows.map(({ label, value, href }) => (
          <div className="legal-row" key={label}>
            <span className="fine">{label}</span>
            {href ? <a href={href}>{value}</a> : <span>{value}</span>}
          </div>
        ))}
      </div>

      <p className="fine">{t('contacts.support')}</p>

      <div className="legal-links">
        <Link to="/offer">{t('legal.offer')}</Link>
        <Link to="/refund">{t('legal.refund')}</Link>
        <Link to="/privacy">{t('legal.privacy')}</Link>
      </div>
    </div>
  );
}
